import NepaliDate from 'nepali-date-converter'

/**
 * Promoted from `screens/portfolio/components/add-transaction/bsToAd.ts`
 * (CRS-PD-4) once the period/date-range picker became a second consumer of
 * BS → AD conversion. That file now just re-exports this one, so its own
 * import and its existing tests keep working unchanged.
 *
 * Bikram Sambat → Gregorian, for the one field on this form the member types in
 * the calendar they actually think in.
 *
 * ── Why this is the riskiest function in the feature ─────────────────────────
 *
 * The API's `date` is AD only, the member enters BS, and a conversion that is
 * wrong by a day does not fail: it books a real trade on the wrong date, which
 * then reprices every later sell through FIFO and shows up as a wrong realised
 * profit weeks later, with nothing on screen having looked broken.
 *
 * `src/lib/bsDate.ts` converts AD → BS only, so this direction is new. It is
 * written as a pure function with no React and no formatting so it can be
 * tested on its own — which matters, because the underlying library has a
 * behaviour that is actively dangerous here.
 *
 * ── The library rolls over instead of rejecting ──────────────────────────────
 *
 * `new NepaliDate(y, m - 1, d)` behaves like `Date`: out-of-range parts CARRY.
 * Verified against nepali-date-converter 3.4.0:
 *
 *     BS 2080-12-31  →  AD 2024-04-13, which is BS 2081-01-01
 *     BS 2081-02-33  →  AD 2024-06-15, which is BS 2081-03-01
 *     BS 2083-13-01  →  AD 2027-04-14, which is BS 2084-01-01
 *     BS 2083-05-00  →  AD 2026-08-16, which is BS 2083-04-31
 *
 * Chaitra 2080 has 30 days and Jestha 2081 has 32, and BS month lengths vary by
 * year — so a member who types the wrong day-of-month for THAT year would
 * silently get a date in the next month, with no error anywhere. Every
 * conversion below is therefore ROUND-TRIPPED through the library's own AD → BS
 * direction and rejected unless all three parts come back unchanged. That turns
 * a silent rollover into a visible "that date does not exist".
 *
 * The library's table covers BS 2000-01-01 … 2090-12-30 and throws outside it;
 * that throw is caught and reported as the same `null`.
 *
 * ── Timezone ────────────────────────────────────────────────────────────────
 *
 * `toJsDate()` returns a Date built from LOCAL calendar fields, so reading it
 * back with `getFullYear`/`getMonth`/`getDate` yields exactly the day that was
 * constructed, in every zone. Verified in Asia/Kathmandu, UTC,
 * America/Los_Angeles and Pacific/Kiritimati: all four give AD 2026-08-18 for
 * BS 2083-05-02. No UTC getter is used anywhere in this file, because that is
 * the one way to reintroduce the off-by-one-day this function exists to avoid.
 */

/** The BS month names the library formats with, in order. Index 0 = Baisakh. */
export const BS_MONTHS = [
  'Baisakh',
  'Jestha',
  'Asar',
  'Shrawan',
  'Bhadra',
  'Aswin',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
] as const

export interface BsParts {
  year: number
  /** 1-12, NOT the library's 0-11 month index. */
  month: number
  day: number
}

const pad = (value: number): string => String(value).padStart(2, '0')

/**
 * Parses what a member may reasonably type into BS parts, or `null`.
 *
 * Two shapes are accepted, both of which appear in this app already:
 *   - numeric: `2083-05-02`, `2083/5/2`, `2083 05 02`
 *   - the way `formatBsDate` renders a date back: `2 Bhadra 2083`
 *
 * Month names are matched case-insensitively on a prefix of at least three
 * letters, so "bhadra", "Bhad" and "BHA" all resolve — a member reading a
 * broker's note should not have to match our spelling exactly. Nothing here
 * validates that the date EXISTS; that is `bsToAd`'s round-trip.
 */
export const parseBsInput = (input: string): BsParts | null => {
  const text = input.trim()
  if (text === '') return null

  const numeric = /^(\d{4})[-/ ](\d{1,2})[-/ ](\d{1,2})$/.exec(text)
  if (numeric !== null) {
    return { year: Number(numeric[1]), month: Number(numeric[2]), day: Number(numeric[3]) }
  }

  const named = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/.exec(text)
  if (named !== null) {
    const prefix = (named[2] ?? '').toLowerCase()
    const index = BS_MONTHS.findIndex((month) => month.toLowerCase().startsWith(prefix))
    if (index < 0) return null
    return { year: Number(named[3]), month: index + 1, day: Number(named[1]) }
  }

  return null
}

/**
 * BS parts → an `YYYY-MM-DD` AD date, or `null` when that BS date does not
 * exist.
 *
 * The round-trip is the validation. See the rollover table above: without it,
 * "31 Chaitra 2080" converts happily to the first day of the NEXT year.
 */
export const bsToAd = (parts: BsParts): string | null => {
  const { year, month, day } = parts
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null
  // Cheap structural rejects first, so an obviously wrong month never reaches a
  // library whose answer for month 13 is "next year".
  if (month < 1 || month > 12 || day < 1 || day > 32) return null

  let converted: Date
  try {
    converted = new NepaliDate(year, month - 1, day).toJsDate()
  } catch {
    // Outside the library's 2000-01-01 … 2090-12-30 table.
    return null
  }

  const back = NepaliDate.fromAD(converted).getBS()
  if (back.year !== year || back.month !== month - 1 || back.date !== day) return null

  // Local getters on purpose — see the timezone note above.
  return `${String(converted.getFullYear())}-${pad(converted.getMonth() + 1)}-${pad(converted.getDate())}`
}

/** `parseBsInput` then `bsToAd`, which is what a form field actually wants. */
export const bsInputToAd = (input: string): string | null => {
  const parts = parseBsInput(input)
  return parts === null ? null : bsToAd(parts)
}

/**
 * An AD `YYYY-MM-DD` rendered as a BS date, for the read-only echo beside the
 * BS field and for prefilling it with today.
 *
 * Deliberately NOT `formatBsDate` from `@/lib/bsDate`: that one answers `'—'`
 * for an unconvertible date, which is right for a table cell and wrong for a
 * form field, where the caller has to be able to tell "no value" from "a value
 * we could not convert".
 */
export const adToBsInput = (iso: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (match === null) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  try {
    const bs = NepaliDate.fromAD(new Date(year, month - 1, day)).getBS()
    return `${String(bs.date)} ${BS_MONTHS[bs.month] ?? ''} ${String(bs.year)}`.trim()
  } catch {
    return null
  }
}

/**
 * The AD date spelled the way a member cross-checking a broker's contract note
 * would read it: "18 August 2026".
 *
 * Built from the parts, never from `toLocaleDateString` on a parsed `Date`:
 * `new Date("2026-08-18")` is UTC midnight, which is the 17th in any western
 * zone, and this line sits directly under a date field whose whole job is to be
 * unambiguous.
 */
const AD_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export const formatAdLong = (iso: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (match === null) return null
  const month = AD_MONTHS[Number(match[2]) - 1]
  if (month === undefined) return null
  return `${String(Number(match[3]))} ${month} ${match[1] ?? ''}`.trim()
}
